import { useState, useEffect } from 'react';

import { getClientGoals } from 'api/generated/client-goals/client-goals';
import { getClientGoalCriticalElements } from 'api/generated/client-goal-critical-elements/client-goal-critical-elements';
import type { ClientGoalDto, CriticalElementDto } from 'api/generated/schemas';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

export type CriticalElement = CriticalElementDto;

export type Goal = ClientGoalDto & { criticalElements?: CriticalElementDto[] };

interface CurrentUserShape {
  data?: {
    oktaUserId?: string;
    client?: { id?: string | number };
  };
}

export interface UseGoalsResult {
  loading: boolean;
  goals: Goal[];
}

const useGoals = (selectedYear: string | number): UseGoalsResult => {
  const [loading, setLoading] = useState(true);
  const [goals, setGoals] = useState<Goal[]>([]);
  const currentUser = useCurrentUser() as CurrentUserShape;

  useEffect(() => {
    const getGoals = async (): Promise<void> => {
      if (!currentUser.data?.oktaUserId || !currentUser.data?.client?.id) return;
      const userId = currentUser.data.oktaUserId;

      setLoading(true);
      try {
        const page = await getClientGoals(userId, Number(currentUser.data.client.id), {
          'enteringClassYear.equals': Number(selectedYear),
          size: 1000,
          sort: ['createdDate,desc'],
        });

        const goalsData = page.content ?? [];

        if (goalsData.length) {
          const goalsWithCriticalElements = await Promise.all(
            goalsData.map(async (goal) => {
              if (goal.id == null) {
                return { ...goal, criticalElements: [] as CriticalElementDto[] };
              }

              try {
                const criticalElementsList = await getClientGoalCriticalElements(
                  userId,
                  goal.id,
                );

                return {
                  ...goal,
                  criticalElements: (criticalElementsList ?? []).map(
                    (criticalElement) => ({
                      ...criticalElement,
                      details: criticalElement.details?.map((detail) => ({
                        ...detail,
                        focusArea: detail.focusArea ?? undefined,
                      })),
                    }),
                  ),
                };
              } catch (error) {
                console.error(
                  `Error fetching critical elements for goal ${goal.id}`,
                  error,
                );

                return { ...goal, criticalElements: [] as CriticalElementDto[] };
              }
            }),
          );

          setGoals(goalsWithCriticalElements);
        } else {
          setGoals([]);
        }
      } catch (error) {
        console.error('An error occurred while fetching goals', error);
      } finally {
        setLoading(false);
      }
    };

    getGoals();
  }, [selectedYear, currentUser.data]);

  return { loading, goals };
};

export default useGoals;
